/**
 * Delade hjälpfunktioner för kontrakts-/dokumentbilagor.
 *
 * - Validerar tillåtna filformat och storlek på klienten.
 * - Konverterar HEIC (iPhone-foton) till JPEG så de kan förhandsgranskas.
 * - Komprimerar/skalar ned stora bilder innan uppladdning för att spara
 *   bandbredd och undvika storleksgränsen.
 *
 * De tunga biblioteken (heic2any, browser-image-compression) importeras lazy
 * så att de bara laddas i webbläsaren när en fil faktiskt behandlas.
 */

export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.xls',
  '.xlsx',
  '.csv',
  '.ppt',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
];

/** Värde till `accept`-attributet på fil-input. */
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.join(',');

export const MAX_ATTACHMENT_SIZE_MB = 25;
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

/** Läsbar lista med versala format, t.ex. "PDF, DOCX, PNG …". */
export const ATTACHMENT_FORMATS_LABEL = ALLOWED_ATTACHMENT_EXTENSIONS.map((ext) =>
  ext.slice(1).toUpperCase(),
).join(', ');

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
// Bilder större än så här komprimeras; mindre bilder lämnas orörda.
const COMPRESS_THRESHOLD_BYTES = 2 * 1024 * 1024;

export type AttachmentErrorReason =
  | 'unsupportedType'
  | 'empty'
  | 'tooLarge'
  | 'processingFailed';

export interface AttachmentError {
  name: string;
  reason: AttachmentErrorReason;
}

export interface PreparedAttachments {
  files: File[];
  errors: AttachmentError[];
}

function extensionOf(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const heic2any = (await import('heic2any')).default;
  const converted = (await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.85,
  })) as Blob | Blob[];
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const newName = file.name.replace(/\.heic$/i, '.jpg');
  return new File([blob], newName, { type: 'image/jpeg' });
}

async function compressImage(file: File): Promise<File> {
  const imageCompression = (await import('browser-image-compression')).default;
  return imageCompression(file, {
    maxSizeMB: 8,
    maxWidthOrHeight: 2560,
    useWebWorker: true,
    initialQuality: 0.8,
  });
}

/**
 * Validerar och förbehandlar bilagor inför uppladdning. Filer som inte klarar
 * valideringen returneras i `errors` (och laddas alltså inte upp).
 */
export async function prepareAttachmentsForUpload(
  input: File[],
): Promise<PreparedAttachments> {
  const files: File[] = [];
  const errors: AttachmentError[] = [];

  for (const original of input) {
    const ext = extensionOf(original.name);

    if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      errors.push({ name: original.name, reason: 'unsupportedType' });
      continue;
    }

    // Tom fil (0 byte) – t.ex. en misslyckad sparning. Fånga tidigt med ett
    // tydligt fel i stället för att skicka en oanvändbar fil.
    if (original.size === 0) {
      errors.push({ name: original.name, reason: 'empty' });
      continue;
    }

    let processed = original;
    try {
      if (ext === '.heic') {
        processed = await convertHeicToJpeg(original);
      }
      const isImage =
        IMAGE_EXTENSIONS.includes(extensionOf(processed.name));
      if (isImage && processed.size > COMPRESS_THRESHOLD_BYTES) {
        processed = await compressImage(processed);
      }
    } catch {
      // Om konvertering/komprimering misslyckas: falla tillbaka på originalet
      // för icke-HEIC; HEIC kan inte laddas upp okonverterat på ett meningsfullt
      // sätt, så det räknas som ett fel.
      if (ext === '.heic') {
        errors.push({ name: original.name, reason: 'processingFailed' });
        continue;
      }
      processed = original;
    }

    if (processed.size > MAX_ATTACHMENT_SIZE_BYTES) {
      errors.push({ name: original.name, reason: 'tooLarge' });
      continue;
    }

    files.push(processed);
  }

  return { files, errors };
}

/** Plockar ut backendens felkod ur ett axios-fel, om den finns. */
export function attachmentErrorCode(error: unknown): string | null {
  const data = (error as { response?: { data?: { error?: { code?: unknown } } } })
    ?.response?.data?.error?.code;
  return typeof data === 'string' ? data : null;
}
